export default function WhySection() {
  return (
    <section id="why" className="py-20 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* セクションタイトル */}
        <div className="text-center mb-16">
          <div className="inline-block px-3 py-1 bg-white rounded-full mb-4">
            <span className="text-xs tracking-wider text-gray-500">WHY</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            なぜ<span className="text-[#8B6BAE]">角度</span>が重要なのか
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            描画角度は、創作の質と身体の健康を左右する重要な要素です
          </p>
        </div>

        {/* 実際の写真セクション */}
        <div className="relative w-full h-[500px] rounded-2xl overflow-hidden mb-12 shadow-lg">
          <img
            src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/9bed4bc5a425b4955d1c09c43a2a86e1.jpeg"
            alt="Apple Pencilを持つ手 - 適切な角度での描画"
            title="Apple Pencil 描画角度 手首の自然な位置"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          
          {/* テキストオーバーレイ */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 px-6">
            <div className="text-center max-w-3xl">
              <p className="text-2xl md:text-3xl font-semibold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                手首を自然な位置に保つことが、<br />長時間の創作活動を支える
              </p>
              <p className="text-sm text-white/80">
                適切な角度は手根管症候群のリスクを軽減し、描画精度を向上させます
              </p>
            </div>
          </div>
        </div>

        {/* 理由カード */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* 健康面 */}
          <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 flex items-center justify-center bg-[#8B6BAE]/10 rounded-xl mb-5">
              <i className="ri-heart-pulse-line text-2xl text-[#8B6BAE]"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              身体への負担を軽減
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              フラットな画面での作業は手首を過度に反らせ、手根管症候群のリスクを高めます。適切な角度は手首を自然な位置に保ち、長時間の創作活動を支えます。
            </p>
          </div>

          {/* 作業効率 */}
          <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 flex items-center justify-center bg-[#D4A574]/10 rounded-xl mb-5">
              <i className="ri-pencil-ruler-2-line text-2xl text-[#D4A574]"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              描画精度の向上
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              最適な角度は手首の可動域を広げ、より自然なストロークを可能にします。細かいディテールも描きやすく、作業効率が大幅に向上します。
            </p>
          </div>

          {/* 視認性 */}
          <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 flex items-center justify-center bg-[#6B9B8A]/10 rounded-xl mb-5">
              <i className="ri-eye-line text-2xl text-[#6B9B8A]"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              視野角の最適化
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              画面を見る角度によって色の見え方や反射が変わります。適切な傾斜は目の疲労を軽減し、正確な色判断をサポートします。
            </p>
          </div>

          {/* 姿勢 */}
          <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 flex items-center justify-center bg-[#7A9BC7]/10 rounded-xl mb-5">
              <i className="ri-body-scan-line text-2xl text-[#7A9BC7]"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              姿勢の改善
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              画面の角度は首や肩の姿勢に直結します。適切な傾斜により、背筋を伸ばした自然な姿勢で作業でき、肩こりや首の痛みを予防します。
            </p>
          </div>
        </div>

        {/* 統計データ */}
        <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-1">10倍</div>
              <div className="text-xs text-gray-500">フラット時の手根管内圧上昇</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-1">25°</div>
              <div className="text-xs text-gray-500">医学的推奨の最大角度</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-1">9種類</div>
              <div className="text-xs text-gray-500">用途別の最適角度</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}